import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface DialogData {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  type: 'submit' | 'quit' | 'warning';
}

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="dialog-container" [ngClass]="data.type">
      <div class="icon-wrapper" [ngClass]="data.type">
        <mat-icon>{{ getIcon() }}</mat-icon>
      </div>
      
      <h2 class="dialog-title">{{ data.title }}</h2>
      
      <p class="dialog-message">{{ data.message }}</p>
      
      <div class="dialog-actions">
        <button class="btn-cancel" (click)="onCancel()">{{ data.cancelText }}</button>
        <button class="btn-confirm" [ngClass]="data.type" (click)="onConfirm()">{{ data.confirmText }}</button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 2.5rem 2rem 2rem;
      text-align: center;
      width: 100%;
      max-width: 380px;
      margin: 0 auto;
      background: white;
      border-radius: 20px;
      position: relative;
      font-family: 'Poppins', sans-serif;
      box-sizing: border-box;
    }

    .icon-wrapper {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 1.5rem;
      position: relative;
      
      &.submit {
        background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
        box-shadow: 0 8px 24px rgba(139, 92, 246, 0.25);
      }
      
      &.quit, &.warning {
        background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
        box-shadow: 0 8px 24px rgba(239, 68, 68, 0.25);
      }
      
      mat-icon {
        font-size: 40px;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      &.submit mat-icon { color: #7c3aed; }
      &.quit mat-icon, &.warning mat-icon { color: #dc2626; }
    }

    .dialog-title {
      margin: 0 0 0.75rem;
      font-size: 1.5rem;
      font-weight: 700;
      color: #111827;
      letter-spacing: -0.02em;
    }

    .dialog-message {
      color: #6b7280;
      font-size: 0.95rem;
      line-height: 1.6;
      margin: 0 0 2rem;
      padding: 0 0.5rem;
    }

    .dialog-actions {
      display: flex;
      gap: 0.75rem;
      justify-content: center;

      button {
        padding: 0.875rem 1.75rem;
        border-radius: 12px;
        font-weight: 600;
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.2s ease;
        border: none;
        white-space: nowrap;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .btn-cancel {
        background: #f3f4f6;
        color: #4b5563;
        
        &:hover { 
          background: #e5e7eb;
          transform: translateY(-1px);
        }
      }

      .btn-confirm {
        color: white;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        
        &.submit {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          
          &:hover { 
            background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(139, 92, 246, 0.35);
          }
        }
        
        &.quit, &.warning {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          
          &:hover { 
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(239, 68, 68, 0.35);
          }
        }
      }
    }

    @media (max-width: 768px) {
      .dialog-container {
        padding: 2rem 1.5rem 1.5rem;
        width: 100%;
        max-width: 90%; // Responsive width
        margin: 0 auto;
      }

      .icon-wrapper {
        width: 64px;
        height: 64px;
        margin-bottom: 1rem;

        mat-icon {
          font-size: 32px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .dialog-title {
        font-size: 1.25rem;
        font-weight: 600; // Reduced boldness
        margin-bottom: 0.5rem;
      }

      .dialog-message {
        font-size: 0.9rem;
        margin-bottom: 1.5rem;
        line-height: 1.5;
      }

      .dialog-actions {
        gap: 0.5rem;

        button {
          padding: 0.75rem 1.25rem; // Reduced padding
          font-size: 0.9rem;
          font-weight: 500; // Reduced boldness
        }
      }
    }

    @media (max-width: 480px) {
      .dialog-actions {
        flex-direction: column;
        width: 100%;
        
        button {
          width: 100%;
          padding: 1.2rem; // Better touch target height for mobile
        }
      }
    }
  `]
})
export class ConfirmationDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) { }

  getIcon(): string {
    if (this.data.type === 'submit') {
      return 'task_alt';
    } else if (this.data.type === 'warning') {
      return 'warning';
    }
    return 'delete_outline';
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
